import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { fmtDate, fmtDateTime } from '../../Utils/format';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
  LinearProgress,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import LaptopOutlinedIcon from '@mui/icons-material/LaptopOutlined';
import HistoryIcon from '@mui/icons-material/History';
import { deviceAPI } from '../../API/deviceAPI';
import { clientAPI } from '../../API/clientAPI';
import { storeAPI } from '../../API/storeAPI';
import { eventAPI } from '../../API/eventAPI';
import { expandSeverityParam } from '../../Utils/eventHelpers';
import DeviceStatusIndicator from '../../Components/GatewayContagem/DeviceStatusIndicator';
import EventsTable from '../../Components/GatewayContagem/EventsTable';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import AccessDeniedState from '../../Components/GatewayContagem/AccessDeniedState';
import NotFoundState from '../../Components/GatewayContagem/NotFoundState';
import EditDeviceModal from '../../Components/Modal/EditDeviceModal';
import DeviceStoreHistoryModal from '../../Components/Modal/DeviceStoreHistoryModal';
import usePermissions from '../../Context/Hooks/usePermissions';
import { PERMISSIONS } from '../../Constants/permissions';
import { isForbiddenError, isNotFoundError } from '../../Utils/apiHelpers';

const PERIODS = ['today', '7d', '30d', 'month', 'custom'];

const computeDatesForPeriod = (period) => {
  const end = new Date();
  const start = new Date();

  if (period === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (period === '7d') {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (period === '30d') {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (period === 'month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else if (period === 'custom') {
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  }

  return { start, end };
};

function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="baseline"
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          minWidth: 140,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          flex: 1,
          minWidth: 0,
          wordBreak: 'break-word',
        }}
      >
        {value ?? '—'}
      </Typography>
    </Stack>
  );
}

const DeviceDetailPage = () => {
  const { t } = useTranslation(['devices', 'dashboard']);
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.DEVICES_MANAGE);

  const initialDates = computeDatesForPeriod('7d');

  const [device, setDevice] = useState(null);
  const [client, setClient] = useState(null);
  const [store, setStore] = useState(null);
  const [storeMap, setStoreMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [isForbidden, setIsForbidden] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [storeHistoryOpen, setStoreHistoryOpen] = useState(false);

  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);

  const [eventsPage, setEventsPage] = useState(0);
  const [eventsItemsPerPage, setEventsItemsPerPage] = useState(10);

  const [eventsSearch, setEventsSearch] = useState('');
  const [debouncedEventsSearch, setDebouncedEventsSearch] = useState('');
  const [eventsSeverityLevel, setEventsSeverityLevel] = useState([]);
  const [eventsStatusFilter, setEventsStatusFilter] = useState(['open']);

  const [period, setPeriod] = useState('7d');
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedEventsSearch(eventsSearch);
    }, 300);

    return () => clearTimeout(timeout);
  }, [eventsSearch]);

  const loadDevice = useCallback(async () => {
    if (!deviceId) {
      return;
    }

    setLoading(true);
    setIsForbidden(false);
    setIsNotFound(false);

    try {
      const result = await deviceAPI.getById(deviceId);

      if (isForbiddenError(result)) {
        setIsForbidden(true);
        return;
      }

      if (isNotFoundError(result)) {
        setIsNotFound(true);
        return;
      }

      if (!result || result.error || !result.value) {
        return;
      }

      const currentDevice = result.value;

      setDevice(currentDevice);

      if (currentDevice.clientId) {
        clientAPI.getById(currentDevice.clientId).then((clientResult) => {
          if (clientResult && !clientResult.error && clientResult.value) {
            setClient(clientResult.value);
          }
        });
        storeAPI.getByClient(currentDevice.clientId).then((storesResult) => {
          if (storesResult && !storesResult.error && Array.isArray(storesResult.value)) {
            const map = {};
            storesResult.value.forEach((s) => {
              if (s.id) {
                map[s.id] = s.name;
                map[String(s.id).toLowerCase()] = s.name;
              }
            });
            setStoreMap(map);
          }
        });
      }

      if (currentDevice.storeId) {
        storeAPI.getById(currentDevice.storeId).then((storeResult) => {
          if (storeResult && !storeResult.error && storeResult.value) {
            setStore(storeResult.value);
          }
        });
      }
    } catch (err) {
      if (isForbiddenError(null, err)) {
        setIsForbidden(true);
      } else if (isNotFoundError(null, err)) {
        setIsNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  const loadEvents = useCallback(async () => {
    if (!deviceId) {
      return;
    }

    setEventsLoading(true);

    const finalEndDate = new Date(endDate);
    finalEndDate.setHours(23, 59, 59, 999);

    const hasOpen = eventsStatusFilter.includes('open');
    const hasResolved = eventsStatusFilter.includes('resolved');
    const isResolved = (hasOpen && hasResolved) || (!hasOpen && !hasResolved)
      ? undefined
      : hasResolved;

    const severityLevelParam = expandSeverityParam(eventsSeverityLevel);

    const result = await eventAPI.list({
      page: eventsPage + 1,
      itemsPerPage: eventsItemsPerPage,
      deviceId,
      search: debouncedEventsSearch,
      severityLevel: severityLevelParam,
      isResolved,
      startDate,
      endDate: finalEndDate,
      sortBy: 'createdAt',
      sortDirection: 'desc',
    });

    setEventsLoading(false);

    if (!result || result.error) {
      setEvents([]);
      setEventsTotal(0);
      return;
    }

    const paginatedResult = result.value;

    setEvents(
      Array.isArray(paginatedResult?.items)
        ? paginatedResult.items
        : [],
    );

    setEventsTotal(paginatedResult?.totalCount ?? 0);
  }, [
    deviceId,
    eventsPage,
    eventsItemsPerPage,
    debouncedEventsSearch,
    eventsSeverityLevel,
    eventsStatusFilter,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    const { start, end } = computeDatesForPeriod(period);

    setStartDate(start);
    setEndDate(end);
    setEventsPage(0);
  }, [period]);

  useEffect(() => {
    setEventsPage(0);
  }, [
    debouncedEventsSearch,
    eventsSeverityLevel,
    eventsStatusFilter,
  ]);

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleSaveComment = async (eventId, note) => {
    const result = await eventAPI.saveComment(eventId, note);
    if (result && !result.error) {
      toast.success(t('events:feedback.commentSaved'));
      loadEvents();
    } else {
      toast.error(t('events:feedback.commentError'));
    }
  };

  const handleResolve = async (eventId) => {
    const result = await eventAPI.resolve(eventId);
    if (result && !result.error) {
      toast.success(t('events:feedback.eventResolved'));
      loadEvents();
      window.dispatchEvent(new CustomEvent('gateway-events-updated'));
    } else {
      toast.error(t('events:feedback.resolveError'));
    }
  };

  const handleMarkAllRead = async () => {
    const result = await eventAPI.resolveAll({ deviceId });
    if (result && !result.error) {
      toast.success(t('events:feedback.allEventsResolved'));
      loadEvents();
      window.dispatchEvent(new CustomEvent('gateway-events-updated'));
    } else {
      toast.error(t('events:feedback.resolveError'));
    }
  };

  const enrichedEvents = useMemo(() => {
    return events.map((event) => {
      const eventStoreId = event.storeId ? String(event.storeId).toLowerCase() : null;
      const resolvedStoreName = eventStoreId
        ? (storeMap[eventStoreId] || (store?.id && String(store.id).toLowerCase() === eventStoreId ? store.name : null))
        : null;

      return {
        ...event,
        deviceName: device?.name || device?.serialNumber || event.deviceId,
        clientName: client?.name || '—',
        storeName: resolvedStoreName || event.storeName || '—',
        brandName: client?.name || '—',
      };
    });
  }, [events, device, client, store, storeMap]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Skeleton variant="text" width={100} height={20} animation="wave" />
          <Skeleton variant="text" width={140} height={20} animation="wave" />
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Skeleton variant="circular" width={36} height={36} animation="wave" />
          <Box>
            <Skeleton variant="text" width={180} height={28} animation="wave" />
            <Skeleton variant="text" width={120} height={18} animation="wave" />
          </Box>
        </Box>
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Skeleton variant="text" width={140} height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              <Stack spacing={1.5} sx={{ flex: 1 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="text" width="80%" height={22} animation="wave" />
                ))}
              </Stack>
              <Stack spacing={1.5} sx={{ flex: 1 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="text" width="80%" height={22} animation="wave" />
                ))}
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isForbidden) {
    return <AccessDeniedState backPath="/Gateway/Devices" />;
  }

  if (isNotFound || !device) {
    return <NotFoundState backPath="/Gateway/Devices" />;
  }

  const periodLabel = (value) => {
    const map = { today: 'today', '7d': 'sevenDays', '30d': 'thirtyDays', month: 'month', custom: 'custom' };
    return t(`dashboard:filters.${map[value] || value}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      {eventsLoading && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            zIndex: 9999,
          }}
        />
      )}

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/Gateway/Devices')}
        >
          {t('devices:title')}
        </Link>

        <Typography
          variant="body2"
          color="text.primary"
        >
          {device.name || device.serialNumber}
        </Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'flex',
          alignItems: {
            xs: 'flex-start',
            md: 'center',
          },
          justifyContent: 'space-between',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
        >
          <IconButton
            onClick={() => navigate('/Gateway/Devices')}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>

          <LaptopOutlinedIcon
            sx={{
              color: 'primary.main',
              fontSize: 32,
            }}
          />

          <Box>
            <Typography
              variant="h2"
              sx={{ mb: 0.25 }}
            >
              {device.name || device.serialNumber || '—'}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              {device.serialNumber} · {client?.name ?? '—'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setStoreHistoryOpen(true)}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'text.secondary' },
            }}
          >
            {t('devices:detail.storeHistory', 'Histórico de Lojas')}
          </Button>
          {canManage && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setEditDialogOpen(true)}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': { borderColor: 'text.secondary' },
              }}
            >
              {t('devices:menu.edit')}
            </Button>
          )}
          <IconButton onClick={loadDevice} size="small" sx={{ color: 'text.secondary' }}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography
            variant="h4"
            mb={1.5}
          >
            {t('devices:detail.infoCard.title')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              gap: {
                xs: 0.75,
                md: 4,
              },
            }}
          >
            <Stack
              spacing={0.75}
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <InfoRow
                label={t('devices:detail.infoCard.status')}
                value={<DeviceStatusIndicator status={device.status} showLabel />}
              />

              <InfoRow
                label={t('devices:detail.infoCard.serialNumber')}
                value={device.serialNumber}
              />

              <InfoRow
                label={t('devices:detail.infoCard.macAddress')}
                value={device.macAddress}
              />

              <InfoRow
                label={t('devices:detail.infoCard.model')}
                value={device.model}
              />

              <InfoRow
                label={t('devices:detail.infoCard.firmware')}
                value={device.firmwareVersion}
              />

              <InfoRow
                label={t('devices:detail.infoCard.ipAddress')}
                value={device.ipAddress}
              />
            </Stack>

            <Stack
              spacing={0.75}
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <InfoRow
                label={t('devices:detail.infoCard.location')}
                value={device.location}
              />

              <InfoRow
                label={t('devices:detail.infoCard.store')}
                value={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <span>{store?.name ?? '—'}</span>
                    <IconButton
                      size="small"
                      onClick={() => setStoreHistoryOpen(true)}
                      title={t('devices:detail.storeHistory', 'Histórico de Lojas')}
                      sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <HistoryIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                }
              />

              <InfoRow
                label={t('devices:detail.infoCard.client')}
                value={client?.name}
              />

              <InfoRow
                label={t('devices:detail.infoCard.installationDate')}
                value={
                  device.installationDate
                    ? fmtDate(device.installationDate)
                    : null
                }
              />

              <InfoRow
                label={t('devices:detail.infoCard.lastHeartbeat')}
                value={
                  device.lastHeartbeat
                    ? fmtDateTime(device.lastHeartbeat)
                    : null
                }
              />
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="h4"
          sx={{ flex: 1 }}
        >
          {t('devices:detail.events.title')}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          gap={1}
        >
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setPeriod(value);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                fontSize: '0.8125rem',
                fontWeight: 500,
                textTransform: 'none',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: 'primary.dark',
                  color: '#fff',
                  borderColor: 'primary.dark',
                  '&:hover': {
                    bgcolor: 'primary.main',
                  },
                },
              },
            }}
          >
            {PERIODS.map((currentPeriod) => (
              <ToggleButton
                key={currentPeriod}
                value={currentPeriod}
              >
                {periodLabel(currentPeriod)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <DateRangePopover
            startDate={startDate}
            endDate={endDate}
            onStartChange={(date) => {
              if (!date) {
                return;
              }

              setPeriod(null);
              setStartDate(date);
              setEventsPage(0);
            }}
            onEndChange={(date) => {
              if (!date) {
                return;
              }

              setPeriod(null);
              setEndDate(date);
              setEventsPage(0);
            }}
            startLabel={t('dashboard:filters.dateStart')}
            endLabel={t('dashboard:filters.dateEnd')}
          />
        </Stack>
      </Box>

      <EventsTable
        events={enrichedEvents}
        totalCount={eventsTotal}
        page={eventsPage}
        rowsPerPage={eventsItemsPerPage}
        search={eventsSearch}
        severityLevel={eventsSeverityLevel}
        statusFilter={eventsStatusFilter}
        loading={eventsLoading}
        onPageChange={(_, newPage) => setEventsPage(newPage)}
        onRowsPerPageChange={(event) => {
          setEventsItemsPerPage(Number(event.target.value));
          setEventsPage(0);
        }}
        onSearchChange={(value) => {
          setEventsSearch(value);
          setEventsPage(0);
        }}
        onSeverityLevelChange={(levels) => {
          setEventsSeverityLevel(levels);
          setEventsPage(0);
        }}
        onStatusFilterChange={(status) => {
          setEventsStatusFilter(status);
          setEventsPage(0);
        }}
        onSaveComment={handleSaveComment}
        onResolve={handleResolve}
        onMarkAllRead={handleMarkAllRead}
      />

      {canManage && (
        <EditDeviceModal
          open={editDialogOpen}
          device={device}
          storeName={store?.name}
          clientName={client?.name}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={() => {
            loadDevice();
          }}
        />
      )}

      <DeviceStoreHistoryModal
        open={storeHistoryOpen}
        onClose={() => setStoreHistoryOpen(false)}
        deviceId={device?.id}
        deviceName={device?.name || device?.serialNumber}
      />
    </Box>
  );
};

export default DeviceDetailPage;
